import React, { useState, useEffect, useRef } from 'react';
import type { RenderLeftCellProps, SelectGames, CutGamesObj } from '../../types';
import SourceTable from '../SourceTable';
import Loading from '@/pages/intelligence/components/Loading';
import { SHORTCUTPCCONSOLELIST } from '../../utils';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { GameSegmentType } from '@/pages/intelligence/pages/PCConsoleBenchmark/types';
import classnames from 'classnames';
import { fetchShortGameListFromType } from '@intelligence/services/compare';
import useSetQueryDefinition from '@intelligence/hooks/useQueryDefinition';
import { condParams } from '@/pages/intelligence/_config/condParams';
import { getGameInfoById } from '@intelligence/services/common';

export default function ShortCutCell(props: RenderLeftCellProps) {
  const {
    noSearchResultTips,
    entityType,
    checkedEntitiesKeys,
    entityKey,
    setIsOverOneThousTips,
    checkedEntities,
    setCheckedEntities,
    entityRenderer,
    chooseUpNum = 20,
    handleLoadMore,
    isMoreLoading,
    gameActive,
    setGameActive,
    hasShortCut,
    isShortCutPage,
    gamesShortList,
    setGamesShortList,
    isDisabled,
    disabledMsg,
    shortcutList = SHORTCUTPCCONSOLELIST,
    onShortcutClick,
    shortcutAddGame = true,
    isShowShortcutTitle = true,
    gamesAllList,
    setNormalPage,
    showEntity,
    shortCutNameEn = false,
  } = props;

  const { formatMessage } = useIntlExtend();
  // 快捷键游戏列表
  const [shortcutGameList, setShortcutGameList] = useState<CutGamesObj[]>([]);
  const [shortLoading, setShortLoading] = useState(true);
  // 获取头部快捷高度
  const [headerHeight, setHeaderHeight] = useState(0); 
  
  const headerRef = useRef<HTMLDivElement | null>(null); // 创建 ref
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight); // 设置高度
    }
  }, [shortLoading, isShowShortcutTitle]);
  

  // 若已有勾选游戏快捷键，重新打开时，回填已选游戏
  const getDefaultGameList = (gameList: CutGamesObj[]) => {
    const listTemp: any = [...new Set([])];
    gameList?.forEach((item: any) => {
      const flag = gameActive?.[item.tag];
      // 如果是取消
      if (flag) {
        listTemp?.push(...item.games);
      }
    });
    setGamesShortList(listTemp);
  };

  const handleCheckedEntitiesKeysChange = (payload: string[], e: any) => {
    // 勾选事件
    if (e.checked) {
      // 超出1000,禁止再选，并提示
      if (Array.isArray(payload) && payload?.length <= chooseUpNum) {
        if (payload?.includes(e.recordKey)) {
          const gamesAllListAddList = [e.record, ...checkedEntities];
          setCheckedEntities(gamesAllListAddList);
        }
      }
    } else {
      const checkedEntitiesTemp = checkedEntities?.filter((item: any) => item?.[entityKey] !== e.recordKey);
      setCheckedEntities(checkedEntitiesTemp);
    }
  };
  // 使用统一全局存储数据
  const { condDatas } = useSetQueryDefinition(condParams);
  // 获取快捷键游戏
  const getShortGameListFromType = async () => {
    setShortLoading(true);
    if (Object.keys(condDatas).length === 0) {
      return;
    }
    try {
      const shortcut = [
        typeof condDatas.gamesCompareShortcut.compare_shortcut_poe_like === 'string' ? JSON.parse(condDatas.gamesCompareShortcut.compare_shortcut_poe_like) : condDatas.gamesCompareShortcut.compare_shortcut_poe_like,
        typeof condDatas.gamesCompareShortcut.compare_shortcut_tarkov_like === 'string' ? JSON.parse(condDatas.gamesCompareShortcut.compare_shortcut_tarkov_like) : condDatas.gamesCompareShortcut.compare_shortcut_tarkov_like,
        typeof condDatas.gamesCompareShortcut.compare_shortcut_dying_light2_related === 'string' ?JSON.parse(condDatas.gamesCompareShortcut.compare_shortcut_dying_light2_related) : condDatas.gamesCompareShortcut.compare_shortcut_dying_light2_related,
        typeof condDatas.gamesCompareShortcut.compare_shortcut_dunerelated === 'string'? JSON.parse(condDatas.gamesCompareShortcut.compare_shortcut_dunerelated): condDatas.gamesCompareShortcut.compare_shortcut_dunerelated,
      ];
      // 通过id 查游戏详情
      let cids: any = [];
      shortcut.forEach((item: any) => {
        cids = [...cids, ...item.games];
      }); // custom
      
      let datas: any = {};
      if (cids.length > 0) {
        const tmp = await getGameInfoById({
          entity_type: 'combine', // 暂时使用combine查询，2025/06/05 之后使用 auto查询
          ids: cids,
          level: 'custom',
          custom_fields: [
            'id',
            'entity_type',
            'cover',
            'entity_name',
            'genre',
            'sub_genre',
            'iegg_genre',
            'iegg_sub_genre',
            'publisher',
            'release_time',
            'release_dates_by_platform',
          ],
        });
        datas = tmp.data;
      }
      const shortcuts = shortcut.map((items: any) => {
        return {
          ...items,
          games: items.games.map((e: any) => datas[e] || e),
        };
      });
      setShortcutGameList(shortcuts);
      getDefaultGameList(shortcuts);
    } catch (exception) {
    } finally {
      setShortLoading(false);
    }
  };

  const handleAddGames = (gamesList: any[], isAdd: boolean) => {
    if (!Array.isArray(gamesList)) return;
    const gameIdList = gamesList?.map((item) => item.id);
    let selectedNum = checkedEntities?.length * 1;
    let newGameList = [...new Set([...checkedEntities])];
    let isShowOver = false;
    gamesList?.forEach((item: SelectGames) => {
      // 如果是添加
      if (isAdd) {
        // 如果已选游戏不存在，才添加
        if (!checkedEntitiesKeys?.includes(item.id)) {
          // 如果没超出最大限制个数
          if (selectedNum < 20) {
            newGameList.push(item);
            selectedNum = selectedNum + 1;
          } else {
            isShowOver = true;
          }
        }
      } else {
        // 如果是取消
        newGameList = newGameList?.filter((item: SelectGames) => !gameIdList?.includes(item.id));
      }
    });
    setIsOverOneThousTips(isShowOver);
    setCheckedEntities(newGameList);
  };

  const handleShortcutClick = async (type: GameSegmentType) => {
    if (onShortcutClick) {
      onShortcutClick(type, !gameActive?.[type]);
      setNormalPage?.(1);
    }
    const scutGameList = shortcutGameList?.find((item: any) => item?.tag === type)?.games || [];
    const selectIdList = scutGameList?.map((item) => item.id);
    const flag = gameActive?.[type];
    // 如果是取消
    if (flag) {
      const targetList = gamesShortList?.filter((item: SelectGames) => !selectIdList?.includes(item.id));
      setGamesShortList(targetList);
      if (shortcutAddGame) {
        handleAddGames(scutGameList, false);
      }
    } else {
      const list = [...new Set([...gamesShortList, ...scutGameList])];
      setGamesShortList(list);
      if (shortcutAddGame) {
        handleAddGames(scutGameList, true);
      }
    }
    setGameActive({
      ...gameActive,
      [type]: !gameActive?.[type],
    });
  };
  // 设置快捷搜索游戏
  useEffect(() => {
    setGamesShortList(gamesAllList);
    setShortLoading(false);
  }, [gamesAllList]);

  // 设置快捷选项游戏
  useEffect(() => {
    if (shortcutList === SHORTCUTPCCONSOLELIST) {
      getShortGameListFromType();
    }
  }, [gamesAllList, condDatas]);

  return (
    <>
      {shortLoading ? (
        <Loading />
      ) : (
        <>
          <div ref={headerRef} className="similar-games-header">
            {isShowShortcutTitle && (
              <div className="selected-header-tit">
                <span>{formatMessage({ id: 'dbi_shortcut' })}</span>
              </div>
            )}
            <div className="selected-header-shortcut">
              {shortcutList?.map((item, index) => (
                <span
                  className={classnames([
                    'shortcut-tag',
                    {
                      active: gameActive?.[item?.value as GameSegmentType] || false,
                    },
                    {
                      disabled: shortLoading,
                    },
                  ])}
                  key={index}
                  onClick={() => handleShortcutClick(item?.value)}
                >
                  {shortCutNameEn ? item?.text : formatMessage({ id: item?.text })}
                </span>
              ))}
            </div>
          </div>
          {gamesShortList?.length > 0 ? (
            <SourceTable
              hideHeader={true}
              entityKey={entityKey}
              records={[...gamesShortList]}
              value={checkedEntitiesKeys}
              entityType={entityType}
              entityRenderer={entityRenderer}
              showEntity={showEntity}
              onChange={handleCheckedEntitiesKeysChange}
              handleLoadMore={handleLoadMore}
              isMoreLoading={isMoreLoading}
              hasShortCut={hasShortCut}
              isShortCutPage={isShortCutPage}
              isDisabled={isDisabled}
              disabledMsg={disabledMsg}
              chooseUpNum={chooseUpNum}
              setCheckedEntities={setCheckedEntities}
              checkedEntities={checkedEntities}
              headerHeight={headerHeight}
            />
          ) : (
            <div className="dbi-table-no-data-shortcut dbi-left-cell">
              <div>
                <div style={{ textAlign: 'center' }}>{formatMessage({ id: noSearchResultTips })}</div>
              </div>
            </div>
          )}
          {gamesShortList?.length > 0 && isMoreLoading && <Loading classNameValue="dbi-sourcetable-loading" />}
        </>
      )}
    </>
  );
}
